import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  FileEdit,
  Radar,
  Clock,
  Building2,
  ArrowRight,
  Zap,
  Rocket,
  Calendar,
  Target,
  AlertTriangle,
  CheckCircle2,
  FolderOpen,
  LayoutTemplate,
  RefreshCw,
  Plus,
  BarChart3,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { grantService, GrantStats, PublicGrant } from '../services/grantService';
import { getApiBaseUrl } from '../utils/api';
import SkeletonLoader from '../components/SkeletonLoader';
import EmptyState from '../components/EmptyState';

interface DashboardProposal {
  id: string;
  title: string;
  grant_title?: string;
  company?: string;
  progress: number;
  status: string;
  updated_at: string;
}

const STATUS_COLORS: Record<string, string> = {
  '초안': 'bg-gray-100 text-gray-700',
  draft: 'bg-gray-100 text-gray-700',
  '작성중': 'bg-blue-100 text-blue-700',
  writing: 'bg-blue-100 text-blue-700',
  '검토중': 'bg-yellow-100 text-yellow-700',
  reviewing: 'bg-yellow-100 text-yellow-700',
  '제출완료': 'bg-green-100 text-green-700',
  submitted: 'bg-green-100 text-green-700',
  '선정': 'bg-emerald-100 text-emerald-700',
  selected: 'bg-emerald-100 text-emerald-700',
};

const STATUS_LABELS: Record<string, string> = {
  draft: '초안',
  writing: '작성중',
  reviewing: '검토중',
  submitted: '제출완료',
  selected: '선정',
  rejected: '탈락',
};


function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 60) return `${minutes}분 전`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}시간 전`;
  const days = Math.floor(hours / 24);
  return `${days}일 전`;
}

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [grantStats, setGrantStats] = useState<GrantStats | null>(null);
  const [urgentGrants, setUrgentGrants] = useState<PublicGrant[]>([]);
  const [proposals, setProposals] = useState<DashboardProposal[]>([]);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  const fetchData = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    setError(null);
    try {
      const results = await Promise.allSettled([
        grantService.getStats(),
        grantService.getGrants({ limit: 5, status: '마감임박' }),
      ]);

      if (results[0].status === 'fulfilled') {
        setGrantStats(results[0].value);
      }
      if (results[1].status === 'fulfilled') {
        setUrgentGrants(results[1].value.items || []);
      }

      // 제안서 데이터 - API 실패 시 빈 배열 (Mock 제거)
      try {
        const token = localStorage.getItem('proposalcraft_access_token');
        const baseUrl = getApiBaseUrl();

        const response = await fetch(`${baseUrl}/api/proposals?limit=10`, {
          headers: { 'Authorization': `Bearer ${token}` },
          signal,
        });
        if (response.ok) {
          const data = await response.json();
          if (data.proposals?.length > 0) {
            setProposals(data.proposals);
            return;
          }
        }
      } catch (err: any) {
        if (err.name === 'AbortError') return; // Cleanup, ignore
        console.error('Dashboard data fetch failed:', err);
      }
      // API 실패 시 빈 배열 (Mock 제거)
      setProposals([]);

      setLastRefresh(new Date());
    } catch (err) {
      setError('데이터를 불러오는 중 오류가 발생했습니다.');
      setProposals([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    fetchData(controller.signal);
    return () => controller.abort();
  }, [fetchData]);

  const quickActions = [
    {
      title: 'AI 사업계획서 작성',
      description: '맥락표 기반 AI 자동 생성',
      icon: Zap,
      path: '/proposal-generator',
      color: 'border-emerald-200 bg-emerald-50',
      iconBg: 'bg-emerald-100 text-emerald-600',
    },
    {
      title: '공모사업 확인',
      description: '실시간 공모사업 모니터링',
      icon: Radar,
      path: '/grant-monitoring',
      color: 'border-blue-200 bg-blue-50',
      iconBg: 'bg-blue-100 text-blue-600',
      badge: grantStats?.urgent,
    },
    {
      title: '포트폴리오 관리',
      description: '기업 데이터 및 포트폴리오',
      icon: FolderOpen,
      path: '/company-pool',
      color: 'border-purple-200 bg-purple-50',
      iconBg: 'bg-purple-100 text-purple-600',
    },
    {
      title: '템플릿 선택',
      description: '문서 유형별 템플릿',
      icon: LayoutTemplate,
      path: '/templates',
      color: 'border-orange-200 bg-orange-50',
      iconBg: 'bg-orange-100 text-orange-600',
    },
  ];

  const totalProposals = proposals.length;
  const completedProposals = proposals.filter(
    (p) => p.status === '제출완료' || p.status === 'submitted' || p.status === '선정' || p.status === 'selected'
  ).length;
  const completionRate = totalProposals > 0 ? Math.round((completedProposals / totalProposals) * 100) : 0;

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* 마감 임박 경고 배너 */}
      {grantStats && grantStats.urgent > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            <span className="text-sm font-medium text-amber-800">
              마감 임박 공고 {grantStats.urgent}건이 있습니다
            </span>
          </div>
          <Link to="/grant-monitoring" className="text-sm font-medium text-amber-700 hover:text-amber-900">
            확인하기 &rarr;
          </Link>
        </div>
      )}

      {/* 에러 배너 */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-center justify-between">
          <span className="text-sm text-red-700">{error}</span>
          <button onClick={() => fetchData()} className="text-sm font-medium text-red-700 hover:text-red-900 flex items-center gap-1">
            <RefreshCw className="w-4 h-4" />
            다시 시도
          </button>
        </div>
      )}

      {/* 헤더 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {user ? `${user.full_name || user.username}님, 안녕하세요` : '대시보드'}
          </h1>
          <p className="text-gray-500 mt-1">AI와 함께 공모사업 제안서를 효율적으로 작성하세요</p>
        </div>
        <Link
          to="/proposal-generator"
          className="hidden sm:flex items-center px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors text-sm font-medium"
        >
          <Plus className="w-4 h-4 mr-1.5" />
          새 제안서
        </Link>
      </div>

      {/* 통계 카드 */}
      {loading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="animate-pulse space-y-3">
                <div className="flex items-center justify-between">
                  <div className="w-10 h-10 bg-gray-200 rounded-lg" />
                  <div className="w-12 h-7 bg-gray-200 rounded" />
                </div>
                <div className="w-20 h-4 bg-gray-200 rounded" />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-2">
              <div className="p-2.5 bg-blue-100 rounded-lg"><Radar className="w-5 h-5 text-blue-600" /></div>
              <span className="text-2xl font-bold text-gray-900">{grantStats?.total || 0}</span>
            </div>
            <p className="text-sm text-gray-500">모니터링 공모사업</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-2">
              <div className="p-2.5 bg-emerald-100 rounded-lg"><FileEdit className="w-5 h-5 text-emerald-600" /></div>
              <span className="text-2xl font-bold text-gray-900">{totalProposals}</span>
            </div>
            <p className="text-sm text-gray-500">전체 제안서</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-2">
              <div className="p-2.5 bg-purple-100 rounded-lg"><BarChart3 className="w-5 h-5 text-purple-600" /></div>
              <span className="text-2xl font-bold text-gray-900">{completionRate}%</span>
            </div>
            <p className="text-sm text-gray-500">완료율</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-2">
              <div className="p-2.5 bg-amber-100 rounded-lg"><AlertTriangle className="w-5 h-5 text-amber-600" /></div>
              <span className="text-2xl font-bold text-gray-900">{grantStats?.urgent || 0}</span>
            </div>
            <p className="text-sm text-gray-500">마감 임박</p>
          </div>
        </div>
      )}

      {/* 빠른 액션 */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {quickActions.map((action) => {
          const Icon = action.icon;
          return (
            <button
              key={action.path}
              onClick={() => navigate(action.path)}
              className={`relative text-left border rounded-xl p-4 hover:shadow-md transition-all group ${action.color}`}
            >
              {action.badge && action.badge > 0 && (
                <span className="absolute top-3 right-3 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                  {action.badge}
                </span>
              )}
              <div className={`p-2 rounded-lg w-fit mb-3 ${action.iconBg}`}>
                <Icon className="w-5 h-5" />
              </div>
              <h3 className="font-semibold text-gray-900 text-sm mb-1">{action.title}</h3>
              <p className="text-xs text-gray-500">{action.description}</p>
              <ArrowRight className="w-4 h-4 text-gray-400 absolute bottom-4 right-4 group-hover:translate-x-1 transition-transform" />
            </button>
          );
        })}
      </div>

      {/* AI 기능 하이라이트 */}
      <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-2xl p-6 border border-emerald-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="p-3 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-xl">
              <Rocket className="h-7 w-7 text-white" />
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-semibold text-gray-900">ProposalCraft AI</h3>
              <p className="text-sm text-gray-600 mt-0.5">
                공모사업 분석 → 기업 매칭 → 맥락표 → 사업계획서 자동 생성
              </p>
            </div>
          </div>
          <Link
            to="/proposal-generator"
            className="hidden md:flex bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-5 py-2.5 rounded-xl text-sm font-medium hover:shadow-lg transition-all items-center"
          >
            시작하기
            <ArrowRight className="h-4 w-4 ml-1.5" />
          </Link>
        </div>
      </div>

      {/* 메인 콘텐츠 2컬럼 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 진행 중인 제안서 */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-gray-900">진행 중인 제안서</h2>
            <Link to="/proposals" className="text-sm text-emerald-600 hover:text-emerald-700 font-medium">
              전체 보기
            </Link>
          </div>

          {loading ? (
            <SkeletonLoader variant="list" count={3} />
          ) : proposals.length === 0 ? (
            <EmptyState
              icon={FileEdit}
              title="제안서가 없습니다"
              description="첫 제안서를 작성해보세요!"
              actionLabel="새 제안서 작성"
              onAction={() => navigate('/proposal-generator')}
            />
          ) : (
            <div className="space-y-3">
              {proposals.map((proposal) => {
                const statusLabel = STATUS_LABELS[proposal.status] || proposal.status;
                const statusColor = STATUS_COLORS[proposal.status] || STATUS_COLORS[statusLabel] || 'bg-gray-100 text-gray-700';
                return (
                  <div
                    key={proposal.id}
                    className="p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                    onClick={() => navigate(`/proposals`)}
                  >
                    <div className="flex items-start justify-between mb-1.5">
                      <h3 className="font-medium text-gray-900 text-sm truncate pr-2">{proposal.title}</h3>
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full flex-shrink-0 ${statusColor}`}>
                        {statusLabel}
                      </span>
                    </div>
                    {proposal.grant_title && (
                      <p className="text-xs text-gray-500 mb-2 truncate">{proposal.grant_title}</p>
                    )}
                    <div className="flex items-center justify-between text-xs text-gray-400 mb-2">
                      {proposal.company && (
                        <span className="flex items-center gap-1">
                          <Building2 className="w-3 h-3" />
                          {proposal.company}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {timeAgo(proposal.updated_at)}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                      <div
                        className="bg-emerald-500 h-1.5 rounded-full transition-all duration-300"
                        style={{ width: `${proposal.progress}%` }}
                      />
                    </div>
                  </div>
                );
              })}
              <button
                onClick={() => navigate('/proposal-generator')}
                className="w-full p-3 border-2 border-dashed border-gray-200 rounded-lg text-sm text-gray-400 hover:border-emerald-300 hover:text-emerald-500 transition-colors flex items-center justify-center gap-1"
              >
                <Plus className="w-4 h-4" />
                새 제안서 추가
              </button>
            </div>
          )}
        </div>

        {/* 공모사업 현황 */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-gray-900">공모사업 현황</h2>
            <Link to="/grant-monitoring" className="text-sm text-emerald-600 hover:text-emerald-700 font-medium">
              전체 보기
            </Link>
          </div>

          {loading ? (
            <SkeletonLoader variant="list" count={3} />
          ) : (
            <>
              {/* 공모사업 요약 */}
              {grantStats && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                  <div className="text-center p-2 bg-gray-50 rounded-lg">
                    <div className="text-lg font-bold text-gray-900">{grantStats.total}</div>
                    <div className="text-xs text-gray-500">전체</div>
                  </div>
                  <div className="text-center p-2 bg-green-50 rounded-lg">
                    <div className="text-lg font-bold text-green-700">{grantStats.active}</div>
                    <div className="text-xs text-gray-500">모집중</div>
                  </div>
                  <div className="text-center p-2 bg-amber-50 rounded-lg">
                    <div className="text-lg font-bold text-amber-700">{grantStats.urgent}</div>
                    <div className="text-xs text-gray-500">마감임박</div>
                  </div>
                </div>
              )}

              {/* 마감 임박 목록 */}
              {urgentGrants.length > 0 ? (
                <div className="space-y-3">
                  {urgentGrants.slice(0, 4).map((grant) => (
                    <div key={grant.id} className="p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="px-1.5 py-0.5 text-xs font-medium rounded bg-indigo-100 text-indigo-700">
                          {grant.source}
                        </span>
                        {grant.d_day !== undefined && grant.d_day <= 7 && (
                          <span className="px-1.5 py-0.5 text-xs font-medium rounded bg-red-100 text-red-700">
                            D-{grant.d_day}
                          </span>
                        )}
                      </div>
                      <h4 className="text-sm font-medium text-gray-900 truncate">{grant.title}</h4>
                      <div className="flex items-center justify-between mt-1 text-xs text-gray-500">
                        <span>{grant.ministry || grant.manage_agency}</span>
                        {grant.deadline && (
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {grant.deadline}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyState
                  icon={Radar}
                  title="마감 임박 공고 없음"
                  description="현재 마감이 임박한 공모사업이 없습니다."
                  actionLabel="공모사업 둘러보기"
                  onAction={() => navigate('/grant-monitoring')}
                />
              )}
            </>
          )}
        </div>
      </div>

      {/* 새로고침 타임스탬프 */}
      <div className="flex justify-end">
        <button
          onClick={() => fetchData()}
          className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1 transition-colors"
        >
          <RefreshCw className="w-3 h-3" />
          마지막 업데이트: {lastRefresh.toLocaleTimeString('ko-KR')}
        </button>
      </div>
    </div>
  );
};

export default Dashboard;
